import React, { useEffect, useReducer, useState } from 'react';
import './mcp.css';
import { useNavigate } from 'react-router-dom';
import { authHeader } from '../../../utils/api';
import McpHeader from '../../../components/mcp/McpHeader';

// ─── Design tokens ───────────────────────────────────────────────────────────
const KICKER = 'text-[10px] font-bold uppercase tracking-[0.14em] text-ink-200';
const NUMBER_INPUT = 'mt-1.5 w-28 rounded-xl border border-paper-300 bg-white px-3.5 py-2.5 text-sm text-ink transition-colors duration-150 focus:border-ink focus:outline-none';
const SELECT_INPUT = 'mt-1.5 w-full rounded-xl border border-paper-300 bg-white px-3.5 py-2.5 text-sm text-ink transition-colors duration-150 focus:border-ink focus:outline-none';
const TEXT_INPUT = 'mt-1.5 w-full rounded-xl border border-paper-300 bg-white px-3.5 py-2.5 text-sm text-ink transition-colors duration-150 focus:border-ink focus:outline-none';
const DATE_INPUT = 'mt-1.5 w-44 rounded-xl border border-paper-300 bg-white px-3.5 py-2.5 text-sm text-ink transition-colors duration-150 focus:border-ink focus:outline-none';

// ─── Flow state ──────────────────────────────────────────────────────────────
const FLOW_CHAPTERS = ['fleet', 'schedule', 'content', 'review'];
const CHAPTER_LABELS = {
  fleet: 'Fleet & Data',
  schedule: 'Schedule',
  content: 'Campaign content',
  review: 'Review',
};
const LAST_CHAPTER_INDEX = FLOW_CHAPTERS.length - 1;

function initialFlowState() {
  return {
    fields: {
      cadence: 'weekly',
      intervalDays: undefined,
      launchTime: '09:00',
      timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC',
      lookbackDays: 7,
      vehicleScope: 'all_active',
      vehicleIds: [],
      fleetGroupId: undefined,
      cycleModel: 'rolling_replace',
      approvalMode: 'auto',
      approvalHoldHours: 24,
      approvalFallback: 'skip',
      logic: 'stop',
      radiusMi: 1,
      stopFilters: { minMin: 0, ignitionOffOnly: false, collapseHotZones: false, overlapRadiusMi: 0 },
    },
    campaign: { campaignName: '', objective: '', adFormat: 'image', headlines: [], images: [], budget: { type: 'daily', amountUsd: 10 } },
    chapterIndex: 0,
    contentStepIndex: 0,
    maxVisitedChapter: 0,
    lookbackTouched: false,
  };
}

function defaultLookbackDays(cadence) {
  if (cadence === 'daily') return 1;
  if (cadence === 'weekly') return 7;
  if (cadence === 'monthly') return 30;
  return 7;
}

function flowReducer(s, a) {
  switch (a.type) {
    case 'set_fields': {
      const fields = { ...s.fields, ...a.patch };
      const lookbackTouched = s.lookbackTouched || a.patch.lookbackDays !== undefined;
      return { ...s, fields, lookbackTouched };
    }
    case 'set_campaign':
      return { ...s, campaign: { ...s.campaign, ...a.patch } };
    case 'set_cadence': {
      const lookbackDays = s.lookbackTouched ? s.fields.lookbackDays : defaultLookbackDays(a.cadence);
      return { ...s, fields: { ...s.fields, cadence: a.cadence, intervalDays: a.intervalDays, lookbackDays } };
    }
    case 'go_chapter':
      return { ...s, chapterIndex: Math.max(0, Math.min(a.index, s.maxVisitedChapter)) };
    case 'next':
      return { ...s, chapterIndex: Math.min(s.chapterIndex + 1, LAST_CHAPTER_INDEX), maxVisitedChapter: Math.max(s.maxVisitedChapter, Math.min(s.chapterIndex + 1, LAST_CHAPTER_INDEX)) };
    case 'back':
      return { ...s, chapterIndex: Math.max(s.chapterIndex - 1, 0) };
    case 'set_content_step':
      return { ...s, contentStepIndex: Math.max(0, a.index) };
    default:
      return s;
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function formatLaunchTime(launchTime) {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(launchTime);
  if (!match) return launchTime;
  const hour24 = Number(match[1]);
  const minute = match[2];
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${minute} ${period}`;
}

const CYCLE_MODEL_LABELS = {
  rolling_replace: 'Rolling replace',
  stacking: 'Stacking flights',
  refresh_in_place: 'Refresh in place',
};

function anchorEchoCopy(fields, now) {
  switch (fields.cadence) {
    case 'daily': return 'Runs every day — anchored to the day you activate.';
    case 'weekly': {
      const weekday = now.toLocaleDateString(undefined, { weekday: 'long' });
      return `Runs every ${weekday} — anchored to the day you activate.`;
    }
    case 'monthly': {
      const day = now.getDate();
      return `Runs on day ${day} of each month — anchored to the day you activate. Shorter months clamp to the last day.`;
    }
    case 'custom': {
      const n = fields.intervalDays;
      return n && n > 0 ? `Runs every ${n} day${n === 1 ? '' : 's'} — anchored to the day you activate.` : 'Runs on a custom interval — anchored to the day you activate.';
    }
    default: return '';
  }
}

function vehicleScopeLabel(fields) {
  if (fields.vehicleScope === 'all_active') return 'All active vehicles';
  if (fields.vehicleScope === 'list') {
    const n = fields.vehicleIds.length;
    return `${n} picked vehicle${n === 1 ? '' : 's'}`;
  }
  return 'Fleet group';
}

function budgetLine(budget) {
  return budget ? `$${budget.amountUsd} ${budget.type}` : 'No budget set';
}

// ─── Chapter 1: Fleet & Data ────────────────────────────────────────────────
const LOGIC_OPTIONS = [
  { id: 'stop', label: 'Stop zones', blurb: 'Target radius zones around individual stops.' },
  { id: 'route', label: 'Route corridors', blurb: 'Target along the paths vehicles actually drove.' },
];
const SCOPE_OPTIONS = [
  { id: 'all_active', label: 'All active vehicles' },
  { id: 'list', label: 'Pick vehicles' },
  { id: 'group', label: 'Fleet group' },
];

function SegmentedControl({ label, options, value, onChange }) {
  return (
    <div>
      <p className={KICKER}>{label}</p>
      <div role="group" aria-label={label} className="mt-2 inline-flex rounded-full border border-paper-300 bg-paper-50 p-1">
        {options.map(o => (
          <button
            key={o.id}
            type="button"
            aria-pressed={value === o.id}
            onClick={() => onChange(o.id)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors duration-150 ${
              value === o.id ? 'bg-ink text-white' : 'text-ink-100 hover:text-ink'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function FleetDataChapter({ fields, campaign, dispatch }) {
  const [vehicles, setVehicles] = useState(null);
  const [vehiclesError, setVehiclesError] = useState(null);
  const [groups, setGroups] = useState(null);
  const [groupsError, setGroupsError] = useState(null);

  function setFields(patch) { dispatch({ type: 'set_fields', patch }); }
  function setStopFilters(patch) { setFields({ stopFilters: { ...fields.stopFilters, ...patch } }); }

  useEffect(() => {
    if (fields.vehicleScope !== 'list' || vehicles !== null) return;
    let cancelled = false;
    fetch('/api/vehicles', { headers: authHeader() })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data.vehicles || data.data || [];
        setVehicles(list.filter(v => v.status === 'active').map(v => ({ id: v.id, name: v.name || v.id })));
      })
      .catch(() => { if (!cancelled) setVehiclesError('Could not load vehicles.'); });
    return () => { cancelled = true; };
  }, [fields.vehicleScope, vehicles]);

  useEffect(() => {
    if (fields.vehicleScope !== 'group' || groups !== null) return;
    let cancelled = false;
    fetch('/api/fleet/groups', { headers: authHeader() })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data.groups || data.data || [];
        setGroups(list);
      })
      .catch(() => { if (!cancelled) setGroupsError('Could not load fleet groups.'); });
    return () => { cancelled = true; };
  }, [fields.vehicleScope, groups]);

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-2">
      <div className="space-y-6 rounded-2xl border border-paper-300 bg-white p-5 shadow-card sm:p-6">
        {/* Logic cards */}
        <div>
          <p className={KICKER}>What should we target?</p>
          <div className="mt-2 space-y-2">
            {LOGIC_OPTIONS.map(o => (
              <label
                key={o.id}
                className={`block cursor-pointer rounded-xl border px-4 py-3 transition-colors duration-150 ${
                  fields.logic === o.id ? 'border-ink bg-paper-200' : 'border-paper-300 bg-white hover:border-ink-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <input type="radio" name="logic" checked={fields.logic === o.id} onChange={() => setFields({ logic: o.id })} className="h-4 w-4 border-paper-300 text-ink" />
                  <span className="text-sm font-bold text-ink">{o.label}</span>
                </span>
                <span className="mt-1 block pl-6 text-[13px] text-ink-200">{o.blurb}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Vehicle scope */}
        <div>
          <SegmentedControl label="Which vehicles?" options={SCOPE_OPTIONS} value={fields.vehicleScope} onChange={vehicleScope => setFields({ vehicleScope })} />
          {fields.vehicleScope === 'list' && (
            <div className="mt-3">
              {vehiclesError && <p className="text-sm text-ember">{vehiclesError}</p>}
              <select multiple aria-label="Pick vehicles" value={fields.vehicleIds}
                onChange={e => setFields({ vehicleIds: Array.from(e.target.selectedOptions).map(o => o.value) })}
                className={`${SELECT_INPUT} h-32`}>
                {(vehicles ?? []).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
              {vehicles !== null && vehicles.length === 0 && <p className="mt-1 text-sm text-ink-200">No active vehicles found.</p>}
            </div>
          )}
          {fields.vehicleScope === 'group' && (
            <div className="mt-3">
              {groupsError && <p className="text-sm text-ember">{groupsError}</p>}
              <select aria-label="Fleet group" value={fields.fleetGroupId ?? ''}
                onChange={e => setFields({ fleetGroupId: e.target.value || undefined })}
                className={SELECT_INPUT}>
                <option value="">Pick a fleet group…</option>
                {(groups ?? []).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Data window */}
        <div>
          <label className={KICKER} htmlFor="lookback-days">Lookback window (days of fleet activity per cycle)</label>
          <input id="lookback-days" type="number" min={1} max={90} value={fields.lookbackDays}
            onChange={e => setFields({ lookbackDays: Number(e.target.value) })} className={NUMBER_INPUT} />
        </div>

        {/* Stop filters / route knobs */}
        {fields.logic === 'stop' ? (
          <div className="space-y-3">
            <p className={KICKER}>Stop filters</p>
            <div>
              <label className={KICKER} htmlFor="min-min">Minimum stop duration (minutes)</label>
              <input id="min-min" type="number" min={0} max={1440} value={fields.stopFilters.minMin}
                onChange={e => setStopFilters({ minMin: Number(e.target.value) })} className={NUMBER_INPUT} />
            </div>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={fields.stopFilters.ignitionOffOnly}
                onChange={e => setStopFilters({ ignitionOffOnly: e.target.checked })}
                className="h-4 w-4 rounded border-paper-300 text-ink" />
              Only count stops with the engine off
            </label>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={fields.stopFilters.collapseHotZones}
                onChange={e => setStopFilters({ collapseHotZones: e.target.checked })}
                className="h-4 w-4 rounded border-paper-300 text-ink" />
              Collapse nearby stops into hot zones
            </label>
          </div>
        ) : (
          <div className="space-y-3">
            <p className={KICKER}>Route filters</p>
            <div>
              <label className={KICKER} htmlFor="overlap-radius">Overlap spacing (mi)</label>
              <select id="overlap-radius" value={fields.stopFilters.overlapRadiusMi}
                onChange={e => setStopFilters({ overlapRadiusMi: Number(e.target.value) })}
                className={SELECT_INPUT}>
                {[0, 0.1, 0.25, 0.5, 1, 2].map(v => <option key={v} value={v}>{v === 0 ? 'None' : `${v} mi`}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* Radius */}
        <div>
          <label className={KICKER} htmlFor="radius-mi">Pin radius (miles)</label>
          <input id="radius-mi" type="range" min={1} max={50} value={fields.radiusMi}
            onChange={e => setFields({ radiusMi: Number(e.target.value) })} className="mt-1.5 w-full" />
          <p className="mt-1 text-sm text-ink-100">{fields.radiusMi} mi</p>
        </div>
      </div>

      {/* Preview pane */}
      <div className="rounded-2xl border border-paper-300 bg-white p-5 shadow-card sm:p-6">
        <p className={KICKER}>Preview</p>
        <div className="mt-3 rounded-xl bg-paper-200 p-6 text-center">
          <div className="mx-auto h-12 w-12 -rotate-45 rounded-[50%_50%_50%_0] shadow-[0_12px_28px_-8px_rgba(31,79,229,0.55)]"
            style={{ background: 'linear-gradient(120deg,#1F4FE5,#29C5FF)' }} />
          <p className="mt-4 text-sm font-semibold text-ink">
            {fields.logic === 'stop' ? 'Stop zones' : 'Route corridors'} · {fields.radiusMi} mi radius
          </p>
          <p className="mt-1 text-sm text-ink-200">
            Last {fields.lookbackDays} days of fleet activity
          </p>
          <p className="mt-3 text-xs text-ink-300">
            Pin count updates when you save the automation.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Chapter 2: Schedule ─────────────────────────────────────────────────────
const CADENCE_OPTIONS = [
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'custom', label: 'Custom' },
];
const CYCLE_MODEL_OPTIONS = [
  { id: 'rolling_replace', label: 'Rolling replace', blurb: 'Fresh campaign each cycle; previous one is paused once the new one is live.' },
  { id: 'stacking', label: 'Stacking', blurb: 'Each cycle runs as its own flight with a lifetime budget and end date.' },
  { id: 'refresh_in_place', label: 'Refresh in place', blurb: 'One ongoing campaign; targeting refreshes each cycle.' },
];

function ScheduleChapter({ fields, campaign, dispatch }) {
  function setFields(patch) { dispatch({ type: 'set_fields', patch }); }
  function pickCadence(cadence) { dispatch({ type: 'set_cadence', cadence, intervalDays: fields.intervalDays }); }
  function setIntervalDays(n) { dispatch({ type: 'set_cadence', cadence: 'custom', intervalDays: n }); }

  return (
    <div className="mt-6 space-y-6 rounded-2xl border border-paper-300 bg-white p-5 shadow-card sm:p-6">
      {/* Cadence */}
      <div>
        <p className={KICKER}>How often should this launch?</p>
        <div className="mt-2 flex flex-wrap gap-4">
          {CADENCE_OPTIONS.map(o => (
            <label key={o.id} className="flex items-center gap-2 text-sm text-ink">
              <input type="radio" name="cadence" checked={fields.cadence === o.id} onChange={() => pickCadence(o.id)} className="h-4 w-4 border-paper-300 text-ink" />
              {o.label}
            </label>
          ))}
        </div>
        {fields.cadence === 'custom' && (
          <div className="mt-3">
            <label className={KICKER} htmlFor="interval-days">Every N days</label>
            <input id="interval-days" type="number" min={1} max={90} value={fields.intervalDays ?? ''}
              onChange={e => setIntervalDays(Number(e.target.value))} className={NUMBER_INPUT} />
          </div>
        )}
        <p className="mt-2 text-sm text-ink-200">{anchorEchoCopy(fields, new Date())}</p>
      </div>

      {/* Launch time + timezone */}
      <div className="flex flex-wrap gap-6">
        <div>
          <label className={KICKER} htmlFor="launch-time">Launch time</label>
          <input id="launch-time" type="time" value={fields.launchTime}
            onChange={e => setFields({ launchTime: e.target.value })} className={NUMBER_INPUT} />
        </div>
        <div className="min-w-[220px] flex-1">
          <label className={KICKER} htmlFor="timezone">Timezone</label>
          <input id="timezone" value={fields.timezone}
            onChange={e => setFields({ timezone: e.target.value })} className={SELECT_INPUT} />
          <p className="mt-1.5 text-sm text-ink-200">Launches at {formatLaunchTime(fields.launchTime)} {fields.timezone}</p>
        </div>
      </div>

      {/* Cycle model */}
      <div>
        <p className={KICKER}>How should each cycle launch?</p>
        <div className="mt-2 space-y-2">
          {CYCLE_MODEL_OPTIONS.map(o => (
            <label key={o.id}
              className={`block cursor-pointer rounded-xl border px-4 py-3 transition-colors duration-150 ${
                fields.cycleModel === o.id ? 'border-ink bg-paper-200' : 'border-paper-300 bg-white hover:border-ink-300'
              }`}>
              <span className="flex items-center gap-2">
                <input type="radio" name="cycleModel" aria-label={o.label} checked={fields.cycleModel === o.id}
                  onChange={() => setFields({ cycleModel: o.id })} className="h-4 w-4 border-paper-300 text-ink" />
                <span className="text-sm font-bold text-ink">{o.label}</span>
              </span>
              <span className="mt-1 block pl-6 text-[13px] text-ink-200">{o.blurb}</span>
            </label>
          ))}
        </div>
        {fields.cycleModel === 'stacking' && (
          <p className="mt-2 rounded-xl bg-paper-200 px-3 py-2 text-[13px] text-ink-200">
            Uses a total-per-cycle (lifetime) budget.
          </p>
        )}
      </div>

      {/* Approval mode */}
      <div>
        <p className={KICKER}>Before each cycle goes live</p>
        <div className="mt-2 flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="radio" name="approvalMode" checked={fields.approvalMode === 'auto'}
              onChange={() => setFields({ approvalMode: 'auto' })} className="h-4 w-4 border-paper-300 text-ink" />
            Launch automatically
          </label>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="radio" name="approvalMode" checked={fields.approvalMode === 'review'}
              onChange={() => setFields({ approvalMode: 'review' })} className="h-4 w-4 border-paper-300 text-ink" />
            Hold for my review
          </label>
        </div>
        {fields.approvalMode === 'review' && (
          <div className="mt-3 space-y-3 rounded-xl bg-paper-200 p-4">
            <div>
              <label className={KICKER} htmlFor="approval-hold-hours">Hold for (hours)</label>
              <input id="approval-hold-hours" type="number" min={1} max={168} value={fields.approvalHoldHours}
                onChange={e => setFields({ approvalHoldHours: Number(e.target.value) })} className={NUMBER_INPUT} />
            </div>
            <div>
              <p className={KICKER}>If I never respond</p>
              <div className="mt-2 flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input type="radio" name="approvalFallback" checked={fields.approvalFallback === 'skip'}
                    onChange={() => setFields({ approvalFallback: 'skip' })} className="h-4 w-4 border-paper-300 text-ink" />
                  Skip this cycle
                </label>
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input type="radio" name="approvalFallback" checked={fields.approvalFallback === 'launch'}
                    onChange={() => setFields({ approvalFallback: 'launch' })} className="h-4 w-4 border-paper-300 text-ink" />
                  Launch anyway
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Optional end date */}
      <div>
        <label className={KICKER} htmlFor="automation-end-at">End date (optional)</label>
        <input id="automation-end-at" type="date"
          value={fields.automationEndAt ? fields.automationEndAt.slice(0, 10) : ''}
          onChange={e => setFields({ automationEndAt: e.target.value ? `${e.target.value}T00:00:00.000Z` : undefined })}
          className={DATE_INPUT} />
        <p className="mt-1 text-sm text-ink-200">Leave blank to run indefinitely.</p>
      </div>
    </div>
  );
}

// ─── Chapter 3: Campaign content ────────────────────────────────────────────
const CONTENT_STEPS = [
  { id: 'name', label: 'Name & objective' },
  { id: 'budget', label: 'Budget' },
  { id: 'format', label: 'Ad format' },
  { id: 'images', label: 'Images' },
  { id: 'text', label: 'Ad text' },
];

function ContentChapter({ campaign, dispatch, stepIndex, onStepChange }) {
  function setCampaign(patch) { dispatch({ type: 'set_campaign', patch }); }
  const step = CONTENT_STEPS[stepIndex] ?? CONTENT_STEPS[0];

  return (
    <div className="mt-6 space-y-6">
      {/* Inner step rail */}
      <div className="flex flex-wrap items-center gap-2">
        {CONTENT_STEPS.map((s, i) => (
          <button key={s.id} type="button" onClick={() => onStepChange(i)}
            className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${
              i === stepIndex ? 'bg-ink text-white' : 'border border-paper-300 bg-paper-50 text-ink-200 hover:text-ink'
            }`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-paper-300 bg-white p-5 shadow-card sm:p-6">
        {step.id === 'name' && (
          <div className="space-y-4">
            <div>
              <label className={KICKER} htmlFor="camp-name">Campaign name</label>
              <input id="camp-name" type="text" value={campaign.campaignName || ''}
                onChange={e => setCampaign({ campaignName: e.target.value })} className={TEXT_INPUT} placeholder="Untitled campaign" />
            </div>
            <div>
              <label className={KICKER} htmlFor="camp-objective">Objective</label>
              <select id="camp-objective" value={campaign.objective || ''}
                onChange={e => setCampaign({ objective: e.target.value })} className={SELECT_INPUT}>
                <option value="">Pick an objective…</option>
                <option value="OUTCOME_AWARENESS">Awareness</option>
                <option value="OUTCOME_TRAFFIC">Traffic</option>
                <option value="OUTCOME_ENGAGEMENT">Engagement</option>
                <option value="OUTCOME_LEADS">Leads</option>
                <option value="OUTCOME_SALES">Sales</option>
              </select>
            </div>
          </div>
        )}

        {step.id === 'budget' && (
          <div className="space-y-4">
            <div>
              <label className={KICKER} htmlFor="budget-type">Budget type</label>
              <select id="budget-type" value={campaign.budget?.type || 'daily'}
                onChange={e => setCampaign({ budget: { ...campaign.budget, type: e.target.value } })}
                className={SELECT_INPUT}>
                <option value="daily">Daily</option>
                <option value="lifetime">Lifetime</option>
              </select>
            </div>
            <div>
              <label className={KICKER} htmlFor="budget-amount">Amount (USD)</label>
              <input id="budget-amount" type="number" min={1} value={campaign.budget?.amountUsd ?? ''}
                onChange={e => setCampaign({ budget: { ...campaign.budget, amountUsd: Number(e.target.value) } })}
                className={NUMBER_INPUT} />
            </div>
          </div>
        )}

        {step.id === 'format' && (
          <div className="space-y-3">
            <p className={KICKER}>Ad format</p>
            {['image', 'carousel', 'video'].map(f => (
              <label key={f} className="flex items-center gap-2 text-sm text-ink">
                <input type="radio" name="adFormat" checked={campaign.adFormat === f}
                  onChange={() => setCampaign({ adFormat: f })} className="h-4 w-4 border-paper-300 text-ink" />
                <span className="capitalize">{f}</span>
              </label>
            ))}
          </div>
        )}

        {step.id === 'images' && (
          <div className="space-y-3">
            <p className={KICKER}>Images</p>
            <p className="text-sm text-ink-200">Upload images from the Build wizard. Images already attached: {(campaign.images ?? []).length}</p>
            <div className="rounded-xl border-2 border-dashed border-paper-300 bg-paper-50 p-8 text-center">
              <p className="text-sm text-ink-300">Drag images here or use the Build wizard to upload.</p>
            </div>
          </div>
        )}

        {step.id === 'text' && (
          <div className="space-y-4">
            <div>
              <label className={KICKER} htmlFor="headline">Primary headline</label>
              <input id="headline" type="text" value={campaign.headlines?.[0] || ''}
                onChange={e => setCampaign({ headlines: [e.target.value, ...(campaign.headlines ?? []).slice(1)] })}
                className={TEXT_INPUT} placeholder="Your ad headline" />
            </div>
            <div>
              <label className={KICKER} htmlFor="body-text">Body text</label>
              <textarea id="body-text" value={campaign.bodyText || ''}
                onChange={e => setCampaign({ bodyText: e.target.value })}
                className={`${TEXT_INPUT} min-h-[100px]`} placeholder="Ad body copy" />
            </div>
          </div>
        )}
      </div>

      {/* Inner footer */}
      <div className="flex items-center justify-between border-t border-paper-300 pt-3.5">
        <button type="button" onClick={() => onStepChange(Math.max(0, stepIndex - 1))}
          disabled={stepIndex === 0}
          className="text-[13px] font-semibold text-ink-200 hover:text-ink disabled:cursor-not-allowed disabled:opacity-40">
          Back
        </button>
        {stepIndex < CONTENT_STEPS.length - 1 ? (
          <button type="button" onClick={() => onStepChange(stepIndex + 1)}
            className="rounded-full bg-sun px-4 py-1.5 text-sm font-bold text-ink shadow-sun disabled:cursor-not-allowed disabled:opacity-40">
            Next
          </button>
        ) : (
          <button type="button" onClick={() => dispatch({ type: 'next' })}
            className="rounded-full bg-sun px-4 py-1.5 text-sm font-bold text-ink shadow-sun">
            Continue to review
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Chapter 4: Review ──────────────────────────────────────────────────────
function ReviewChapter({ campaign, fields, dispatch, onDone }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function submit(activate) {
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        name: campaign.campaignName || 'Untitled automation',
        cadence: fields.cadence,
        intervalDays: fields.intervalDays ?? null,
        launchTime: fields.launchTime,
        timezone: fields.timezone,
        cycleModel: fields.cycleModel,
        approvalMode: fields.approvalMode,
        approvalHoldHours: fields.approvalHoldHours,
        approvalFallback: fields.approvalFallback,
        logic: fields.logic,
        radiusMi: fields.radiusMi,
        lookbackDays: fields.lookbackDays,
        vehicleScope: fields.vehicleScope,
        vehicleIds: fields.vehicleIds,
        fleetGroupId: fields.fleetGroupId,
        stopFilters: fields.stopFilters,
        campaignName: campaign.campaignName,
        objective: campaign.objective,
        adFormat: campaign.adFormat,
        budget: campaign.budget,
        headlines: campaign.headlines,
        bodyText: campaign.bodyText,
        status: activate ? 'active' : 'draft',
      };
      const res = await fetch('/api/automations', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...authHeader() },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error?.message || data?.message || 'Could not create the automation.');
        return;
      }
      const id = data?.automation?.id || data?.id;
      onDone?.(id);
    } catch {
      setError('Could not reach the server. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const CARD = 'rounded-2xl border border-paper-300 bg-white p-5 shadow-card';
  const EDIT_LINK = 'text-xs font-semibold text-ink-200 underline underline-offset-2 hover:text-ink hover:no-underline';

  return (
    <div className="mt-6 space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div className={CARD}>
            <div className="flex items-center justify-between">
              <p className={KICKER}>Rules</p>
              <button type="button" onClick={() => dispatch({ type: 'go_chapter', index: 0 })} className={EDIT_LINK}>Edit</button>
            </div>
            <p className="mt-2 text-sm font-semibold text-ink">
              {fields.logic === 'stop' ? 'Stop zones' : 'Route corridors'} · {vehicleScopeLabel(fields)}
            </p>
            <p className="text-sm text-ink-100">Last {fields.lookbackDays} days · {fields.radiusMi} mi radius</p>
          </div>

          <div className={CARD}>
            <div className="flex items-center justify-between">
              <p className={KICKER}>Schedule</p>
              <button type="button" onClick={() => dispatch({ type: 'go_chapter', index: 1 })} className={EDIT_LINK}>Edit</button>
            </div>
            <p className="mt-2 text-sm font-semibold text-ink">{anchorEchoCopy(fields, new Date())}</p>
            <p className="text-sm text-ink-100">{formatLaunchTime(fields.launchTime)} · {fields.timezone}</p>
            <p className="text-sm text-ink-100">{CYCLE_MODEL_LABELS[fields.cycleModel]}</p>
            <p className="text-sm text-ink-100">
              {fields.approvalMode === 'auto'
                ? 'Launches automatically'
                : `Held for review ${fields.approvalHoldHours}h, then ${fields.approvalFallback === 'skip' ? 'skipped' : 'launched anyway'}`}
            </p>
            {fields.automationEndAt && <p className="text-sm text-ink-100">Ends {fields.automationEndAt.slice(0, 10)}</p>}
          </div>

          <div className={CARD}>
            <div className="flex items-center justify-between">
              <p className={KICKER}>Campaign</p>
              <button type="button" onClick={() => dispatch({ type: 'go_chapter', index: 2 })} className={EDIT_LINK}>Edit</button>
            </div>
            <p className="mt-2 text-sm font-semibold text-ink">{campaign.campaignName || 'Untitled campaign'}</p>
            <p className="text-sm text-ink-100">{campaign.objective || '—'} · {campaign.adFormat ?? 'image'}</p>
            <p className="text-sm text-ink-100">{budgetLine(campaign.budget)}</p>
            <p className="text-sm text-ink-100">{(campaign.headlines ?? []).filter(Boolean).length} headlines · {(campaign.images ?? []).length} images</p>
          </div>
        </div>

        {/* Preview */}
        <div className="rounded-2xl border border-paper-300 bg-white p-5 shadow-card">
          <p className={KICKER}>Ad preview</p>
          <div className="mt-3 rounded-xl border border-paper-300 bg-paper-50 p-4">
            <div className="aspect-square rounded-lg bg-paper-200 flex items-center justify-center text-ink-300 text-sm">
              {(campaign.images ?? []).length === 0 ? 'No image' : `${(campaign.images ?? []).length} image(s)`}
            </div>
            <p className="mt-3 text-sm font-bold text-ink">{campaign.headlines?.[0] || 'Your headline here'}</p>
            <p className="mt-1 text-xs text-ink-200">{campaign.bodyText || 'Your body text here'}</p>
          </div>
        </div>
      </div>

      {error && <p className="text-sm text-ember">{error}</p>}

      <div className="flex flex-wrap gap-3">
        <button type="button" disabled={submitting} onClick={() => submit(false)}
          className="rounded-full border border-paper-300 bg-white px-4 py-2 text-sm font-semibold text-ink-100 hover:border-ink-300 disabled:cursor-not-allowed disabled:opacity-50">
          {submitting ? 'Saving…' : 'Save as draft'}
        </button>
        <button type="button" disabled={submitting} onClick={() => submit(true)}
          className="rounded-full bg-sun px-4 py-2 text-sm font-bold text-ink shadow-sun disabled:cursor-not-allowed disabled:opacity-40">
          {submitting ? 'Creating…' : 'Create automation'}
        </button>
      </div>
    </div>
  );
}

// ─── Main flow shell ─────────────────────────────────────────────────────────
const McpAutomationFlow = () => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(flowReducer, undefined, initialFlowState);
  const activeChapter = FLOW_CHAPTERS[state.chapterIndex] ?? 'fleet';

  function handleDone(id) {
    navigate(id ? `/client/mcp/automations` : '/client/mcp/automations');
  }

  return (
    <div className="mcp-page" style={{ padding: 0 }}>
      <McpHeader title="New automation" />

      <div className="mx-auto w-full max-w-5xl p-6 md:p-8">
        {/* Chapter rail */}
        <nav aria-label="Automation flow progress" className="flex items-center gap-2">
          {FLOW_CHAPTERS.map((chapter, i) => {
            const isCurrent = chapter === activeChapter;
            const isDone = i < state.chapterIndex;
            const reached = isDone || isCurrent;
            const clickable = i <= state.maxVisitedChapter;
            const inner = (
              <>
                <span className={`mb-1 flex w-fit items-center gap-1.5 ${isCurrent ? '-mx-2 rounded-full bg-paper-200 px-2' : ''}`}>
                  <span aria-hidden="true"
                    className={`inline-flex h-[18px] w-[18px] items-center justify-center rounded-full text-[10px] font-bold ${reached ? 'bg-ink text-white' : 'bg-paper-300 text-ink-200'}`}>
                    {i + 1}
                  </span>
                  <span aria-current={isCurrent ? 'step' : undefined}
                    className={`text-xs ${isCurrent ? 'font-bold text-ink' : isDone ? 'font-semibold text-ink' : 'text-ink-200'}`}>
                    {CHAPTER_LABELS[chapter]}
                  </span>
                </span>
                <span aria-hidden="true" className={`block h-1 rounded-full ${reached ? 'bg-ink' : 'bg-paper-300'}`} />
              </>
            );
            return clickable ? (
              <button key={chapter} type="button" onClick={() => dispatch({ type: 'go_chapter', index: i })}
                className="flex-1 text-left transition-opacity duration-150 ease-out hover:opacity-80">
                {inner}
              </button>
            ) : (
              <span key={chapter} className="block flex-1 text-left">{inner}</span>
            );
          })}
        </nav>

        {/* Active chapter */}
        {activeChapter === 'fleet' && <FleetDataChapter fields={state.fields} campaign={state.campaign} dispatch={dispatch} />}
        {activeChapter === 'schedule' && <ScheduleChapter fields={state.fields} campaign={state.campaign} dispatch={dispatch} />}
        {activeChapter === 'content' && (
          <ContentChapter campaign={state.campaign} fields={state.fields} dispatch={dispatch}
            stepIndex={state.contentStepIndex} onStepChange={index => dispatch({ type: 'set_content_step', index })} />
        )}
        {activeChapter === 'review' && (
          <ReviewChapter campaign={state.campaign} fields={state.fields} dispatch={dispatch}
            previewSummary={{ pinCount: null }} onDone={handleDone} />
        )}

        {/* Footer */}
        <div className="sticky bottom-0 mt-6 flex items-center justify-between border-t border-paper-300 bg-paper py-3.5">
          <button type="button" onClick={() => dispatch({ type: 'back' })} disabled={state.chapterIndex === 0}
            className="text-[13px] font-semibold text-ink-200 hover:text-ink disabled:cursor-not-allowed disabled:opacity-40">
            Back
          </button>
          {activeChapter !== 'content' && (
            <button type="button" onClick={() => dispatch({ type: 'next' })} disabled={state.chapterIndex === LAST_CHAPTER_INDEX}
              className="rounded-full bg-sun px-4 py-1.5 text-sm font-bold text-ink shadow-sun disabled:cursor-not-allowed disabled:opacity-40">
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default McpAutomationFlow;
